import { useEffect, useMemo, useRef, useState } from "react";
import {
  ActionButton,
  GlassPanel,
  SegmentedControl,
  UtilityButton,
} from "@guandan/ui";
import type { RoomSettings, VoiceMode } from "@guandan/protocol";
import { QRCodeSVG } from "qrcode.react";
import {
  createPlatformService,
  type LobbyRoom,
  type PlatformService,
  type PublicRoomView,
} from "../lib/platform-api";
import { SpectatorRoom } from "./SpectatorRoom";

type Tab = "create" | "join" | "public";
type Difficulty = "standard" | "expert" | "master";
type RoomTemplate = "classic" | "quick" | "master";
type FinishMode = "fixed-rounds" | "through-ace";

interface SpectatorTarget {
  readonly room: PublicRoomView;
  readonly password?: string;
}

interface RoomDraft {
  template: RoomTemplate;
  name: string;
  finishMode: FinishMode;
  rounds: number;
  turnSeconds: number;
  password: string;
  allowBots: boolean;
  allowSpectators: boolean;
  difficulty: Difficulty;
  pause: boolean;
  voiceMode: VoiceMode;
  public: boolean;
}

export interface RoomInvitation {
  readonly code: string;
  readonly token?: string;
}

interface FriendRoomsProps {
  readonly profile: { readonly email: string; readonly nickname: string };
  readonly canCreateVoiceRoom: boolean;
  readonly invitation?: RoomInvitation | null;
  readonly initialRoom?: LobbyRoom | null;
  readonly onInitialRoomConsumed?: () => void;
  readonly onInvitationConsumed?: () => void;
  readonly onBack: () => void;
  readonly onStart: (
    roomId: string,
    roomCode: string,
    settings: RoomSettings,
  ) => void;
}

const initialDraft: RoomDraft = {
  template: "classic",
  name: "好友默契局",
  finishMode: "through-ace",
  rounds: 8,
  turnSeconds: 60,
  password: "",
  allowBots: true,
  allowSpectators: false,
  difficulty: "expert",
  pause: true,
  voiceMode: "push-to-talk",
  public: false,
};

const roomTemplates: Record<
  RoomTemplate,
  Pick<
    RoomDraft,
    | "name"
    | "finishMode"
    | "rounds"
    | "turnSeconds"
    | "allowBots"
    | "allowSpectators"
    | "difficulty"
    | "pause"
    | "voiceMode"
  >
> = {
  classic: {
    name: "好友默契局",
    finishMode: "through-ace",
    rounds: 8,
    turnSeconds: 60,
    allowBots: true,
    allowSpectators: false,
    difficulty: "expert",
    pause: true,
    voiceMode: "push-to-talk",
  },
  quick: {
    name: "四局闪电场",
    finishMode: "fixed-rounds",
    rounds: 4,
    turnSeconds: 15,
    allowBots: true,
    allowSpectators: false,
    difficulty: "standard",
    pause: false,
    voiceMode: "muted",
  },
  master: {
    name: "打过 A 大师场",
    finishMode: "through-ace",
    rounds: 8,
    turnSeconds: 60,
    allowBots: true,
    allowSpectators: false,
    difficulty: "master",
    pause: true,
    voiceMode: "open-mic",
  },
};

export function FriendRooms({
  profile,
  canCreateVoiceRoom,
  invitation,
  initialRoom,
  onInitialRoomConsumed,
  onInvitationConsumed,
  onBack,
  onStart,
}: FriendRoomsProps) {
  const service = useMemo(
    () => createPlatformService(profile),
    [profile.email, profile.nickname],
  );
  const [tab, setTab] = useState<Tab>(invitation ? "join" : "create");
  const [code, setCode] = useState(invitation?.code ?? "");
  const [joinPassword, setJoinPassword] = useState("");
  const [pendingInviteToken, setPendingInviteToken] = useState(
    invitation?.token,
  );
  const [room, setRoom] = useState<LobbyRoom | null>(initialRoom ?? null);
  const [inviteToken, setInviteToken] = useState<string | undefined>(
    initialRoom?.inviteToken,
  );
  const [inviteQrData, setInviteQrData] = useState<string | undefined>(
    initialRoom?.qrData,
  );
  const [ready, setReady] = useState(initialRoom?.selfMember.ready ?? false);
  const [draft, setDraft] = useState<RoomDraft>(() => ({
    ...initialDraft,
    voiceMode: canCreateVoiceRoom ? initialDraft.voiceMode : "muted",
  }));
  const [publicRooms, setPublicRooms] = useState<readonly PublicRoomView[]>([]);
  const [spectatorTarget, setSpectatorTarget] =
    useState<SpectatorTarget | null>(null);
  const [spectatorPasswordRoom, setSpectatorPasswordRoom] =
    useState<PublicRoomView | null>(null);
  const [spectatorPassword, setSpectatorPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const invitationAttempted = useRef(false);
  const gameOpened = useRef(false);
  const qrContainer = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialRoom) onInitialRoomConsumed?.();
  }, [initialRoom, onInitialRoomConsumed]);

  useEffect(() => {
    if (!invitation || invitationAttempted.current) return;
    invitationAttempted.current = true;
    setCode(invitation.code);
    setPendingInviteToken(invitation.token);
    onInvitationConsumed?.();
    void join({
      code: invitation.code,
      ...(invitation.token ? { inviteToken: invitation.token } : {}),
    });
  }, [invitation, onInvitationConsumed]);

  const roomId = room?.id;
  useEffect(() => {
    if (!roomId) return;
    let disposed = false;
    const refresh = async () => {
      try {
        const current = await service.refreshRoom(roomId);
        if (disposed) return;
        setRoom(current);
        setReady(current.selfMember.ready);
        if (current.phase === "playing" && !gameOpened.current) {
          gameOpened.current = true;
          onStart(current.id, current.code, current.settings);
        }
      } catch (cause) {
        if (!disposed) setError(messageOf(cause, "同步房间失败，请重试"));
      }
    };
    const timer = window.setInterval(() => void refresh(), 2_000);
    return () => {
      disposed = true;
      window.clearInterval(timer);
    };
  }, [onStart, roomId, service]);

  useEffect(() => {
    if (tab !== "public" || room) return;
    void loadPublicRooms(service, setPublicRooms, setLoadingRooms, setError);
  }, [room, service, tab]);

  const currentInviteToken = inviteToken ?? pendingInviteToken;
  const inviteUrl = useMemo(() => {
    if (!room) return "";
    if (inviteQrData) return new URL(inviteQrData, location.origin).toString();
    const url = new URL(location.origin);
    url.searchParams.set("room", room.code);
    if (currentInviteToken) url.searchParams.set("invite", currentInviteToken);
    return url.toString();
  }, [currentInviteToken, inviteQrData, room]);

  if (spectatorTarget && service.spectator) {
    return (
      <SpectatorRoom
        gateway={service.spectator}
        room={spectatorTarget.room}
        {...(spectatorTarget.password
          ? { password: spectatorTarget.password }
          : {})}
        onExit={() => {
          setSpectatorTarget(null);
          void loadPublicRooms(
            service,
            setPublicRooms,
            setLoadingRooms,
            setError,
          );
        }}
      />
    );
  }

  if (room) {
    const isOwner = room.ownerId === service.identity.userId;
    const allHumansReady =
      room.members.length > 0 && room.members.every((member) => member.ready);
    const occupiedSeats = new Set([
      ...room.members.map((member) => member.seat),
      ...room.players.map((player) => player.seat),
    ]).size;
    const canFillTable = room.settings.allowBots || occupiedSeats === 4;
    const canStart = allHumansReady && canFillTable;
    return (
      <main className="sub-screen room-wait">
        <header className="sub-header">
          <div>
            <span>
              {service.mode === "remote" ? "联网好友房" : "本地好友房"}
            </span>
            <h1>{room.settings.name}</h1>
          </div>
          <UtilityButton disabled={busy} onClick={() => void leaveRoom()}>
            退出房间
          </UtilityButton>
        </header>
        <GlassPanel className="waiting-panel">
          <div className="room-invite">
            <div className="room-code">
              <span>六位房号</span>
              <strong>{room.code}</strong>
              <button type="button" onClick={() => void copyInvite()}>
                复制邀请链接
              </button>
            </div>
            {inviteUrl ? (
              <div
                className="invite-qr"
                ref={qrContainer}
                data-qr-payload={inviteUrl}
                data-qr-source={inviteQrData ? "service" : "room"}
              >
                <QRCodeSVG
                  value={inviteUrl}
                  size={176}
                  level="M"
                  marginSize={2}
                  title={`好友房 ${room.code} 邀请二维码`}
                />
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => downloadQr(room.code)}
                >
                  下载二维码
                </button>
              </div>
            ) : null}
          </div>
          <p className="local-service-note">
            {service.mode === "remote"
              ? "已连接权威房间服务 · 座位、准备和电脑玩家均由服务器确认"
              : "本地可玩模式 · 座位与电脑玩家会持久保存在当前设备"}
          </p>
          <div className="seat-ring" aria-label="房间座位">
            {([0, 1, 2, 3] as const).map((seat) => {
              const member = room.members.find(
                (candidate) => candidate.seat === seat,
              );
              const player = room.players.find(
                (candidate) => candidate.seat === seat,
              );
              if (player?.isBot)
                return (
                  <div className="seat seat--bot" key={seat}>
                    <b>{player.nickname}</b>
                    <span>
                      电脑玩家 · {difficultyName(room.settings.botDifficulty)}
                    </span>
                    {isOwner && room.phase === "waiting" ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void controlBot(seat, true)}
                      >
                        移除 AI
                      </button>
                    ) : null}
                  </div>
                );
              if (member)
                return (
                  <div
                    className={`seat${member.userId === service.identity.userId ? " seat--self" : ""}`}
                    key={seat}
                  >
                    <b>{member.nickname}</b>
                    <span>
                      {member.ready ? "已准备" : "等待准备"}
                      {member.role === "owner" ? " · 房主" : ""}
                    </span>
                  </div>
                );
              return (
                <div className="seat" key={seat}>
                  <b>空位</b>
                  <span>
                    {room.settings.allowBots
                      ? "可加入真人或电脑"
                      : "仅等待真人加入"}
                  </span>
                  {isOwner &&
                  room.settings.allowBots &&
                  room.phase === "waiting" ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void controlBot(seat, false)}
                    >
                      添加 AI
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
          <p className="room-summary">
            {finishModeName(room.settings)} · {room.settings.turnSeconds} 秒出牌
            · {voiceModeName(room.settings.voiceMode)} ·{" "}
            {room.settings.allowBots
              ? `${difficultyName(room.settings.botDifficulty)} AI`
              : "仅真人"}{" "}
            · {room.settings.allowPause ? "允许暂停" : "不可暂停"}
          </p>
          {error ? (
            <p className="form-error" role="alert">
              {error}
            </p>
          ) : null}
          {notice ? (
            <p className="form-success" role="status">
              {notice}
            </p>
          ) : null}
          <div className="button-row">
            <ActionButton
              tone={ready ? "ghost" : "jade"}
              disabled={busy || room.phase !== "waiting"}
              onClick={() => void toggleReady()}
            >
              {busy ? "同步中…" : ready ? "取消准备" : "准备"}
            </ActionButton>
            {isOwner ? (
              <ActionButton
                tone="orange"
                disabled={busy || !canStart || room.phase !== "waiting"}
                onClick={() => void startRoom()}
              >
                {!allHumansReady
                  ? "等待真人准备"
                  : !canFillTable
                    ? "等待四名真人"
                    : "开始牌局"}
              </ActionButton>
            ) : (
              <span className="muted">全员准备后由房主开局</span>
            )}
          </div>
        </GlassPanel>
      </main>
    );
  }

  function patch<K extends keyof RoomDraft>(key: K, value: RoomDraft[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function applyTemplate(template: RoomTemplate) {
    setDraft((current) => ({
      ...current,
      template,
      ...roomTemplates[template],
      voiceMode: canCreateVoiceRoom
        ? roomTemplates[template].voiceMode
        : "muted",
    }));
  }

  async function createRoom() {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const created = await service.createRoom(
        toRoomSettings(draft, canCreateVoiceRoom),
        draft.password || undefined,
      );
      setRoom(created);
      setInviteToken(created.inviteToken);
      setInviteQrData(created.qrData);
      setReady(created.selfMember.ready);
    } catch (cause) {
      setError(messageOf(cause, "创建房间失败，请重试"));
    } finally {
      setBusy(false);
    }
  }

  async function join(input: {
    code?: string;
    roomId?: string;
    inviteToken?: string;
  }) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const joined = await service.joinRoom({
        ...input,
        ...(joinPassword ? { password: joinPassword } : {}),
      });
      setRoom(joined);
      setReady(joined.selfMember.ready);
      if (input.inviteToken) setInviteToken(input.inviteToken);
    } catch (cause) {
      setTab("join");
      setError(messageOf(cause, "加入房间失败，请核对房号或密码"));
    } finally {
      setBusy(false);
    }
  }

  async function toggleReady() {
    if (!room) return;
    setBusy(true);
    setError("");
    try {
      const updated = await service.setReady(room, !ready);
      setRoom(updated);
      setReady(updated.selfMember.ready);
    } catch (cause) {
      setError(messageOf(cause, "准备状态同步失败"));
    } finally {
      setBusy(false);
    }
  }

  async function startRoom() {
    if (!room) return;
    setBusy(true);
    setError("");
    try {
      const started = await service.startRoom(room);
      setRoom(started);
      if (started.phase === "playing" && !gameOpened.current) {
        gameOpened.current = true;
        onStart(started.id, started.code, started.settings);
      }
    } catch (cause) {
      setError(messageOf(cause, "开局失败，请确认所有真人已准备"));
    } finally {
      setBusy(false);
    }
  }

  async function controlBot(seat: 0 | 1 | 2 | 3, remove: boolean) {
    if (!room) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const updated = remove
        ? await service.removeBot(room, seat)
        : await service.addBot(room, seat, room.settings.botDifficulty);
      setRoom(updated);
      setNotice(
        remove
          ? `已移除 ${seat + 1} 号座位的 AI`
          : `已在 ${seat + 1} 号座位加入 AI`,
      );
    } catch (cause) {
      setError(messageOf(cause, remove ? "移除 AI 失败" : "添加 AI 失败"));
    } finally {
      setBusy(false);
    }
  }

  async function leaveRoom() {
    if (!room) return;
    setBusy(true);
    setError("");
    try {
      await service.leaveRoom(room.id);
      onBack();
    } catch (cause) {
      setError(messageOf(cause, "退出房间失败"));
      setBusy(false);
    }
  }

  async function copyInvite() {
    setError("");
    setNotice("");
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setNotice("邀请链接已复制");
    } catch (cause) {
      setError(messageOf(cause, "复制失败，请手动分享房号"));
    }
  }

  function downloadQr(roomCode: string) {
    setError("");
    setNotice("");
    const svg = qrContainer.current?.querySelector("svg");
    if (!svg) {
      setError("二维码尚未生成，请稍后重试");
      return;
    }
    const source = `<?xml version="1.0" encoding="UTF-8"?>\n${svg.outerHTML}`;
    const objectUrl = URL.createObjectURL(
      new Blob([source], { type: "image/svg+xml;charset=utf-8" }),
    );
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = `guandan-room-${roomCode}.svg`;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    setNotice("二维码已下载");
  }

  const passwordInvalid =
    draft.password.length > 0 && draft.password.length < 4;
  const tabs = [
    { value: "create", label: "创建房间" },
    { value: "join", label: "加入房间" },
    { value: "public", label: "公开列表" },
  ] as const;
  return (
    <main className="sub-screen">
      <header className="sub-header">
        <div>
          <span>好友同桌</span>
          <h1>好友房</h1>
        </div>
        <UtilityButton onClick={onBack}>返回大厅</UtilityButton>
      </header>
      <GlassPanel className="room-browser">
        <SegmentedControl
          label="好友房操作"
          options={tabs}
          value={tab}
          onChange={setTab}
        />
        <p className="local-service-note">
          {service.mode === "remote"
            ? "联网权威服务 · 房间、成员、准备与开局均由服务器确认"
            : "未配置远端会话，使用可持久化的本地可玩服务"}
        </p>
        {error ? (
          <p className="form-error" role="alert">
            {error}
          </p>
        ) : null}
        {tab === "create" ? (
          <form
            className="room-form"
            onSubmit={(event) => {
              event.preventDefault();
              void createRoom();
            }}
          >
            <label className="field">
              <span>玩法模板</span>
              <select
                value={draft.template}
                onChange={(event) =>
                  applyTemplate(event.target.value as RoomTemplate)
                }
              >
                <option value="classic">经典好友局</option>
                <option value="quick">四局闪电场</option>
                <option value="master">打过 A 大师场</option>
              </select>
              <small>选择模板后仍可逐项调整</small>
            </label>
            <label className="field">
              <span>房间名称</span>
              <input
                maxLength={24}
                value={draft.name}
                onChange={(event) => patch("name", event.target.value)}
              />
              <small>最多 24 个字符</small>
            </label>
            <div className="form-grid room-form-grid">
              <label className="field">
                <span>赛制</span>
                <select
                  value={draft.finishMode}
                  onChange={(event) =>
                    patch("finishMode", event.target.value as FinishMode)
                  }
                >
                  <option value="fixed-rounds">固定局数</option>
                  <option value="through-ace">打过 A</option>
                </select>
                <small>按所选赛制自动结算</small>
              </label>
              <label className="field">
                <span>固定局数</span>
                <select
                  aria-label="固定局数"
                  disabled={draft.finishMode !== "fixed-rounds"}
                  value={draft.rounds}
                  onChange={(event) =>
                    patch("rounds", Number(event.target.value))
                  }
                >
                  <option value={1}>1 局体验</option>
                  <option value={4}>4 局</option>
                  <option value={8}>8 局</option>
                  <option value={16}>16 局</option>
                </select>
                <small>
                  {draft.finishMode === "through-ace"
                    ? "打过 A 不使用固定局数"
                    : "达到局数后结算"}
                </small>
              </label>
              <label className="field">
                <span>出牌时间</span>
                <select
                  value={draft.turnSeconds}
                  onChange={(event) =>
                    patch("turnSeconds", Number(event.target.value))
                  }
                >
                  <option value={15}>15 秒</option>
                  <option value={30}>30 秒</option>
                  <option value={60}>60 秒</option>
                </select>
                <small>每位玩家单次出牌限时</small>
              </label>
              <label className="field">
                <span>AI 难度</span>
                <select
                  disabled={!draft.allowBots}
                  value={draft.difficulty}
                  onChange={(event) =>
                    patch("difficulty", event.target.value as Difficulty)
                  }
                >
                  <option value="standard">标准</option>
                  <option value="expert">高手</option>
                  <option value="master">大师</option>
                </select>
                <small>
                  {draft.allowBots
                    ? "用于手动加入与开局自动补位"
                    : "仅真人房不启用 AI"}
                </small>
              </label>
              <label className="field">
                <span>全桌语音</span>
                <select
                  disabled={!canCreateVoiceRoom}
                  value={draft.voiceMode}
                  onChange={(event) =>
                    patch("voiceMode", event.target.value as VoiceMode)
                  }
                >
                  <option value="push-to-talk">按住说话</option>
                  <option value="open-mic">常开麦</option>
                  <option value="muted">关闭语音</option>
                </select>
                <small>
                  {canCreateVoiceRoom
                    ? "仅限当前牌桌全桌交流"
                    : "普通玩家可创建完整牌局，但不能创建 LiveKit 语音房"}
                </small>
              </label>
              <label className="field">
                <span>可选密码</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  minLength={4}
                  maxLength={64}
                  value={draft.password}
                  onChange={(event) => patch("password", event.target.value)}
                />
                <small>
                  {passwordInvalid ? "密码至少 4 位" : "留空即不设密码"}
                </small>
              </label>
            </div>
            <div className="toggle-list">
              <Toggle
                label="公开招募"
                checked={draft.public}
                onChange={(value) => patch("public", value)}
              />
              <Toggle
                label="AI 自动补位（默认开启）"
                checked={draft.allowBots}
                onChange={(value) => patch("allowBots", value)}
              />
              {service.spectator ? (
                <Toggle
                  label="允许开局后观战"
                  checked={draft.allowSpectators}
                  onChange={(value) => patch("allowSpectators", value)}
                />
              ) : null}
              <Toggle
                label="允许暂停"
                checked={draft.pause}
                onChange={(value) => patch("pause", value)}
              />
            </div>
            <ActionButton
              tone="blue"
              size="lg"
              wide
              type="submit"
              disabled={busy || !draft.name.trim() || passwordInvalid}
            >
              {busy ? "创建中…" : "创建房间"}
            </ActionButton>
          </form>
        ) : null}
        {tab === "join" ? (
          <div className="join-panel">
            <label className="field">
              <span>六位房号</span>
              <input
                className="room-code-input"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(event) => {
                  setCode(event.target.value.replace(/\D/g, "").slice(0, 6));
                  setPendingInviteToken(undefined);
                }}
              />
            </label>
            <label className="field">
              <span>房间密码（如有）</span>
              <input
                type="password"
                autoComplete="current-password"
                value={joinPassword}
                onChange={(event) => setJoinPassword(event.target.value)}
              />
            </label>
            <ActionButton
              tone="blue"
              size="lg"
              disabled={busy || code.length !== 6}
              onClick={() =>
                void join({
                  code,
                  ...(pendingInviteToken
                    ? { inviteToken: pendingInviteToken }
                    : {}),
                })
              }
            >
              {busy ? "加入中…" : "加入房间"}
            </ActionButton>
          </div>
        ) : null}
        {tab === "public" ? (
          <div className="public-rooms">
            {spectatorPasswordRoom ? (
              <form
                className="spectator-password"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!spectatorPassword) return;
                  setSpectatorTarget({
                    room: spectatorPasswordRoom,
                    password: spectatorPassword,
                  });
                  setSpectatorPasswordRoom(null);
                  setSpectatorPassword("");
                }}
              >
                <label className="field">
                  <span>{spectatorPasswordRoom.name} · 观战密码</span>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={spectatorPassword}
                    onChange={(event) =>
                      setSpectatorPassword(event.target.value)
                    }
                  />
                </label>
                <div className="button-row">
                  <ActionButton
                    tone="violet"
                    size="sm"
                    type="submit"
                    disabled={!spectatorPassword}
                  >
                    进入只读观战
                  </ActionButton>
                  <ActionButton
                    tone="ghost"
                    size="sm"
                    onClick={() => {
                      setSpectatorPasswordRoom(null);
                      setSpectatorPassword("");
                    }}
                  >
                    取消
                  </ActionButton>
                </div>
              </form>
            ) : null}
            {loadingRooms ? (
              <p>正在获取公开房间…</p>
            ) : publicRooms.length === 0 ? (
              <div className="empty-state">
                <h2>暂无公开房间</h2>
                <p>
                  列表来自当前{service.mode === "remote" ? "服务器" : "设备"}
                  ，不会展示虚构在线房间。
                </p>
                <ActionButton
                  tone="blue"
                  onClick={() =>
                    void loadPublicRooms(
                      service,
                      setPublicRooms,
                      setLoadingRooms,
                      setError,
                    )
                  }
                >
                  刷新
                </ActionButton>
              </div>
            ) : (
              publicRooms.map((publicRoom) => (
                <article key={publicRoom.id}>
                  <div>
                    <strong>{publicRoom.name}</strong>
                    <span>
                      房号 {publicRoom.code} ·{" "}
                      {publicRoom.status === "playing"
                        ? "牌局进行中"
                        : publicRoom.passwordProtected
                          ? "需要密码"
                          : "无需密码"}
                    </span>
                  </div>
                  <p>{publicRoom.humanCount}/4 人</p>
                  {publicRoom.status === "waiting" ? (
                    <ActionButton
                      tone="blue"
                      size="sm"
                      disabled={busy}
                      onClick={() => {
                        if (publicRoom.passwordProtected) {
                          setCode(publicRoom.code);
                          setTab("join");
                          setError("请输入该公开房间的密码");
                        } else {
                          void join({ roomId: publicRoom.id });
                        }
                      }}
                    >
                      加入
                    </ActionButton>
                  ) : publicRoom.status === "playing" &&
                    publicRoom.allowSpectators &&
                    service.spectator ? (
                    <ActionButton
                      tone="violet"
                      size="sm"
                      disabled={busy}
                      onClick={() => {
                        if (publicRoom.passwordProtected) {
                          setSpectatorPasswordRoom(publicRoom);
                          setSpectatorPassword("");
                        } else setSpectatorTarget({ room: publicRoom });
                      }}
                    >
                      {publicRoom.passwordProtected ? "输入密码观战" : "观战"}
                    </ActionButton>
                  ) : (
                    <span className="public-room-status">暂不可进入</span>
                  )}
                </article>
              ))
            )}
          </div>
        ) : null}
      </GlassPanel>
    </main>
  );
}

function toRoomSettings(
  draft: RoomDraft,
  canCreateVoiceRoom: boolean,
): RoomSettings {
  return {
    name: draft.name.trim(),
    visibility: draft.public ? "public" : "private",
    passwordProtected: Boolean(draft.password),
    ruleProfile: {
      id: "classic",
      wildcardInStraightFlush: true,
      antiTributeBigJokerCount: 2,
      allowA2345Straight: true,
      straightFlushBeatsBombSize: 5,
      finishMode: draft.finishMode,
      fixedRounds: draft.finishMode === "fixed-rounds" ? draft.rounds : 8,
    },
    turnSeconds: draft.turnSeconds,
    allowBots: draft.allowBots,
    botDifficulty: draft.difficulty,
    allowSpectators: draft.allowSpectators,
    allowPause: draft.pause,
    voiceMode: canCreateVoiceRoom ? draft.voiceMode : "muted",
  };
}

function finishModeName(settings: RoomSettings): string {
  return settings.ruleProfile.finishMode === "through-ace"
    ? "打过 A"
    : `${settings.ruleProfile.fixedRounds} 局`;
}

function difficultyName(difficulty: Difficulty): string {
  if (difficulty === "master") return "大师";
  if (difficulty === "expert") return "高手";
  return "标准";
}

function voiceModeName(mode: VoiceMode): string {
  if (mode === "open-mic") return "常开麦";
  if (mode === "push-to-talk") return "按住说话";
  return "语音关闭";
}

async function loadPublicRooms(
  service: PlatformService,
  update: (rooms: readonly PublicRoomView[]) => void,
  setLoading: (loading: boolean) => void,
  setError: (message: string) => void,
) {
  setLoading(true);
  setError("");
  try {
    update(await service.listPublicRooms());
  } catch (cause) {
    setError(messageOf(cause, "获取公开房间失败，请重试"));
  } finally {
    setLoading(false);
  }
}

function messageOf(cause: unknown, fallback: string): string {
  return cause instanceof Error && cause.message ? cause.message : fallback;
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="toggle">
      <span>{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
    </label>
  );
}
